// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * React hook for initializing and using the audit logger
 *
 * This hook automatically initializes the audit logger with the current
 * user and organization context, and provides convenient methods for
 * logging application-level events.
 */

import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useEffect, useRef } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { auditLogger } from '../services/auditLogger';

/**
 * Hook to initialize the audit logger with current user context
 *
 * Call this once in a component that has access to both MedplumProvider
 * and OrganizationProvider context.
 */
export function useAuditLoggerInit(): void {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { currentOrganization } = useOrganization();
  const initializedRef = useRef(false);
  const lastOrgIdRef = useRef<string | undefined>(undefined);

  // Initialize audit logger when profile is available
  useEffect(() => {
    if (!profile) return;

    const practitionerId = profile.id;
    const practitionerName =
      profile.resourceType === 'Practitioner' && profile.name?.[0]
        ? `${profile.name[0].given?.join(' ') || ''} ${profile.name[0].family || ''}`.trim()
        : undefined;

    auditLogger.initialize(medplum, {
      organizationId: currentOrganization?.id,
      practitionerId,
      practitionerName,
    });

    // Log application start only once per session
    if (!initializedRef.current) {
      initializedRef.current = true;
      auditLogger.logApplicationStart();
    }
  }, [medplum, profile, currentOrganization?.id]);

  // Update context when organization changes
  useEffect(() => {
    if (currentOrganization?.id && lastOrgIdRef.current !== currentOrganization.id) {
      // Log practice switch if this isn't the initial load
      if (lastOrgIdRef.current !== undefined) {
        auditLogger.logPracticeSwitch(
          lastOrgIdRef.current,
          currentOrganization.id,
          undefined, // We don't have the old org name easily available
          currentOrganization.name
        );
      }
      lastOrgIdRef.current = currentOrganization.id;
      auditLogger.updateContext({ organizationId: currentOrganization.id });
    }
  }, [currentOrganization?.id, currentOrganization?.name]);
}

/**
 * Hook to get the audit logger instance
 *
 * Use this in components that need to log specific events.
 *
 * @example
 * ```tsx
 * function ExportButton() {
 *   const audit = useAuditLogger();
 *
 *   const handleExport = async () => {
 *     const records = await exportData();
 *     audit.logDataExport('patient_records', records.length, patientId);
 *   };
 *
 *   return <Button onClick={handleExport}>Export</Button>;
 * }
 * ```
 */
export function useAuditLogger(): typeof auditLogger {
  return auditLogger;
}
